import type { HeroCustomFieldProperty, HeroCustomFieldValue } from "@/lib/projects";

export interface HeroCustomFieldEntry {
  schemaId: string;
  property: HeroCustomFieldProperty;
}

export function heroCustomFieldKey(schemaId: string, propertyUuid: string): string {
  return `${schemaId}|${propertyUuid}`;
}

export function entryKey(entry: HeroCustomFieldEntry): string {
  return heroCustomFieldKey(entry.schemaId, entry.property.uuid);
}

export function normalizeHeroFieldType(type: string | undefined | null): string {
  return String(type || "text").toLowerCase();
}

export function heroFieldOptions(property: HeroCustomFieldProperty): string[] {
  if (!Array.isArray(property.options)) return [];
  return (property.options as unknown[])
    .map((o) => String(o ?? "").trim())
    .filter(Boolean);
}

export function parseHeroFieldValue(
  raw: unknown,
  type: string,
): string | boolean {
  const t = normalizeHeroFieldType(type);
  if (t === "checkbox") {
    if (raw == null || raw === "" || raw === false || raw === "false") return false;
    return (
      raw === true ||
      raw === "true" ||
      raw === 1 ||
      raw === "1" ||
      raw === "wahr"
    );
  }
  if (raw == null || raw === "") return "";
  return String(raw);
}

export function findHeroCustomFieldMatch(
  fields: HeroCustomFieldValue[] | null | undefined,
  schemaId: string,
  propertyUuid: string,
): HeroCustomFieldValue | undefined {
  const list = Array.isArray(fields) ? fields : [];
  const sid = String(schemaId);
  const pid = String(propertyUuid);
  return list.find((cf) => {
    const cfSchemaId = String(cf.schemaId ?? cf.schema_id ?? "");
    const cfPropertyUuid = String(cf.propertyUuid ?? cf.property_uuid ?? cf.uuid ?? "");
    return cfSchemaId === sid && cfPropertyUuid === pid;
  });
}

export function readHeroFieldFromProject(
  fields: HeroCustomFieldValue[] | null | undefined,
  schemaId: string,
  propertyUuid: string,
  type: string,
): string | boolean {
  const match = findHeroCustomFieldMatch(fields, schemaId, propertyUuid);
  return parseHeroFieldValue(match?.value, type);
}

/** Wert für PUT /hero-custom-fields (wie klassisches collectHeroCustomFieldEditorValues). */
export function serializeHeroFieldValue(
  value: string | boolean | undefined,
  type: string,
): string {
  const t = normalizeHeroFieldType(type);
  if (t === "checkbox") {
    return value === true || value === "true" ? "true" : "false";
  }
  return String(value ?? "").trim();
}

export function sortHeroFieldEntries(entries: HeroCustomFieldEntry[]): HeroCustomFieldEntry[] {
  return [...entries].sort((a, b) => {
    if (a.property.position !== b.property.position) {
      return (a.property.position || 0) - (b.property.position || 0);
    }
    return (a.property.label || "").localeCompare(b.property.label || "", "de");
  });
}
